import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { SubmissionError } from 'redux-form';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { acceptApplication, acceptApplicationFail, acceptApplicationSuccess, editApplication, editApplicationFail, editApplicationSuccess, getApartmentApplications, getApartmentApplicationsFail, getApartmentApplicationsSuccess, getApplication, getApplicationFail, getApplicationSuccess, listApplications, listApplicationsFail, listApplicationsSuccess, rejectApplication, rejectApplicationFail, rejectApplicationSuccess, updateApplicationAdminNotes, updateApplicationAdminNotesFail, updateApplicationAdminNotesSuccess, updateApplicationSuccess, selectApplications, selectApplicationsSuccess, selectApplicationsFail, updateApplicationStatusByCode, updateApplicationStatusByCodeFail, updateApplicationStatusByCodeSuccess, updateApplicationStatus, updateApplicationStatusFail, updateApplicationStatusSuccess, createApplicationFail, createApplication, createApplicationSuccess, updateApplicationPhoneShownByCodeSuccess, updateApplicationPhoneShownByCode, getApartmentLeadSuccess, getApartmentLeadFail, getApartmentLead, getConversationSuccess, getConversationFail, getConversation } from '../actions/applications';
import { closeDialog } from '../actions/dialogs';
import { navigate } from '../actions/routes';
// TODO: Automaattinen tilin luonti - otetaan käyttöön Sendgrid-integraation yhteydessä
// import { openDialog } from '../actions/dialogs';
// import { signupSuccess } from '../actions/users';
// import { formatPhoneNumForCognito } from '../libs/conversions';
// import { authSignup } from '../services/auth/auth';
// import { SignupProps } from '../services/auth/types';
// import { getCurrentLanguage } from '../translations/i18n';
import { UpdateStatusByCodeFormData } from '../containers/Application/Forms/UpdateApplicationStatusByCodeForm';
import { Action } from '../reducers/types';
import { selectSelectedApartmentId } from '../selectors/apartments';
import { selectSelectedApplications } from '../selectors/applications';
import { selectContentIdFromPathname, selectUserIdFromPathname, selectApplicationCodeFromPathname } from '../selectors/router';
import { selectReserveData } from '../selectors/showings';
import ApplicantAvailabilityApi from '../services/applicantAvailability';
import Api from '../services/applications';
import { getHistory } from '../store';
import i18n from '../translations/i18n';
import { AddApplicantAvailabilityFormData } from '../types/applicantAvailability';
import { Application, ApplicationData, CreateApplicationData, CensoredApplication, PartnerLead } from '../types/applications';
import { FormPayloadParameters } from '../types/formData';
import { ReserveData } from '../types/showings';
import { validateIsAdmin } from '../utils/validation/users';
import { uploadFileToS3 } from '../services/storage/s3';
import config from '../config/config';
// TODO: Automaattinen tilin luonti - otetaan käyttöön Sendgrid-integraation yhteydessä
// const generateTempPassword = (): string => {
//     const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
//     const special = '!@#$%&*';
//     let password = '';
//     const array = new Uint8Array(10);
//     crypto.getRandomValues(array);
//     for (let i = 0; i < 10; i++) {
//         password += chars[array[i] % chars.length];
//     }
//     password += special[Math.floor(Math.random() * special.length)];
//     password += String(Math.floor(Math.random() * 10));
//     return password;
// };

export function* handleGetApplication({ payload: { apartmentId, applicationId } }: Action): SagaIterator {
    try {
        const application: Application = yield call(Api.getApplication, apartmentId, applicationId);
        yield put(getApplicationSuccess(application));
    }
    catch (error: any) {
        yield put(getApplicationFail((error as any).message));
    }
}
export function* handleListApplications(): SagaIterator {
    try {
        const applications: Application[] = yield call(Api.getApplications);
        yield put(listApplicationsSuccess(applications));
    }
    catch (error: any) {
        yield put(listApplicationsFail((error as any).message));
    }
}
export function* handleGetApartmentApplications({ payload: { apartmentId } }: any): SagaIterator {
    try {
        const selectedApartmentId: string | undefined = yield select(selectSelectedApartmentId);
        // if admin view, use selected apartmentId from state, otherwise use apartmentId from action payload
        const currentApartmentId = apartmentId || selectedApartmentId;
        const applications: Application[] = yield call(Api.getApartmentApplications, currentApartmentId);
        yield put(getApartmentApplicationsSuccess({ apartmentId, applications }));
    }
    catch (error: any) {
        yield put(getApartmentApplicationsFail((error as any).message));
    }
}
function* accept(apartmentId: string, applicationId: string, confirmImmediately?: boolean) {
    const application: Application = yield call(Api.acceptApplication, apartmentId, applicationId, confirmImmediately);
    yield put(updateApplicationSuccess(application));
}
export function* acceptSelectedApplications(confirmImmediately?: boolean, passedApplicationIds?: string[]) {
    const apartmentId: string = yield select(selectContentIdFromPathname);
    const applicationIds: string[] = yield select(selectSelectedApplications);
    if (passedApplicationIds)
        yield all(passedApplicationIds.map((id) => accept(apartmentId, id, confirmImmediately)));
    else if (applicationIds)
        yield all(applicationIds.map((id) => accept(apartmentId, id, confirmImmediately)));
}
export function* handleAcceptApplication({ payload: { apartmentId, applicationId } }: any): SagaIterator {
    try {
        const application: Application = yield call(Api.acceptApplication, apartmentId, applicationId);
        toast.info(i18n.t('applications.acceptSuccess'), { autoClose: 15000 });
        yield put(updateApplicationSuccess(application));
        yield put(acceptApplicationSuccess());
    }
    catch (error: any) {
        yield call(toast.error, i18n.t('error.application.acceptFail'));
        yield put(acceptApplicationFail((error as any).message));
    }
}
export function* handleRejectApplication({ payload: { apartmentId, applicationId, resolve, reject } }: any): SagaIterator {
    try {
        const application: Application = yield call(Api.rejectApplication, apartmentId, applicationId);
        toast.info(i18n.t('applications.rejectSuccess'), { autoClose: 15000 });
        yield put(updateApplicationSuccess(application));
        yield put(rejectApplicationSuccess());
        if (resolve) {
            yield call(resolve);
        }
    }
    catch (error: any) {
        yield call(toast.error, i18n.t('error.application.rejectFail'));
        yield put(rejectApplicationFail((error as any).message));
        if (reject) {
            yield call(reject, error);
        }
    }
}
export function* handleCreateApplication({ payload }: any): SagaIterator {
    const actionPayload = payload && typeof payload === 'object' && payload !== null && 'formData' in payload
        ? payload
        : { formData: payload };
    const { formData, resolve, reject } = actionPayload;
    try {
        const tempFormData = JSON.parse(JSON.stringify(formData));
        delete tempFormData.time;
        delete tempFormData.dateRange;
        delete tempFormData.phone;
        if (formData.profilePicture) {
            const bucket = config.s3.profilePictures.BUCKET;
            const region = config.s3.profilePictures.REGION;
            const profilePictureUrl = yield call(uploadFileToS3, formData.profilePicture, bucket, region);
            tempFormData.profilePicture = profilePictureUrl.key;
        }
        tempFormData.phone = formData.phone.phone;
        const applicationId = formData.applicationId || '';
        const createApplication: ApplicationData = tempFormData;
        const createdApplication = yield call(Api.createApplication, formData.apartmentCode, formData.language, '', createApplication, applicationId);
        if (formData.availabilities) {
            yield all(formData.availabilities.map((element: any, index: number) => {
                if (index === formData.availabilities.length - 1) {
                    return null;
                }
                const availability: AddApplicantAvailabilityFormData = {
                    apartmentId: createdApplication.apartmentId,
                    applicationId: createdApplication.applicationId,
                    startDate: element.startDate,
                    endDate: element.endDate,
                    startTime: dayjs(element.timeRange.startTime).format(),
                    endTime: dayjs(element.timeRange.endTime).format()
                };
                return call(ApplicantAvailabilityApi.createAvailabilities, availability as any);
            }));
        }
        else {
            const availability: AddApplicantAvailabilityFormData = {
                apartmentId: createdApplication.apartmentId,
                applicationId: createdApplication.applicationId,
                startDate: formData.dateRange.startDate,
                endDate: formData.dateRange.endDate,
                startTime: formData.time.startTime.format(),
                endTime: formData.time.endTime.format()
            };
            yield call(ApplicantAvailabilityApi.createAvailabilities, availability as any);
        }
        // SuperLike payment temporarily disabled
        // const shouldOpenPaymentPopup = Boolean(formData?.superLike)
        const shouldOpenPaymentPopup = false;
        yield put(createApplicationSuccess(createdApplication));
        if (resolve) {
            yield call(resolve, {
                application: createdApplication,
                shouldOpenPaymentPopup
            });
        }
        // TODO: Automaattinen tilin luonti vuokralaiselle - otetaan käyttöön Sendgrid-integraation yhteydessä
        // let tempPassword: string | null = null;
        // try {
        //     const email = formData.email?.toLowerCase();
        //     const name = `${formData.firstName} ${formData.lastName}`;
        //     const phone = formatPhoneNumForCognito(formData.phone?.phone || formData.phone);
        //     const language = getCurrentLanguage();
        //     tempPassword = generateTempPassword();
        //     const signupInfo: SignupProps = {
        //         email,
        //         password: tempPassword,
        //         name,
        //         phone_number: phone,
        //         'custom:language': language,
        //         'custom:role': 'tenant'
        //     };
        //     yield call(authSignup, signupInfo);
        //     yield put(signupSuccess(email));
        // } catch (signupError) {
        //     tempPassword = null;
        // }
        // if (tempPassword) {
        //     localStorage.setItem('leea_temp_password', tempPassword);
        //     yield put(openDialog('TenantAccountCreated'));
        // }
        if (shouldOpenPaymentPopup) {
            yield put(navigate(`/application/${createdApplication.code}/payment`));
            return;
        }
        yield put(navigate(`/aftershowing/${createdApplication.code}/steps/1?newApplication=true`));
    }
    catch (error: any) {
        const errorMessage = error?.response?.status === 409
            ? i18n.t('newApplication.errorDuplicate')
            : (error as any).message;
        yield put(createApplicationFail(errorMessage));
        if (reject) {
            yield call(reject, new SubmissionError({
                _error: errorMessage
            }));
        }
    }
}
export function* handleEditApplicationPhoneShown({ payload: application }: Action<Application>): SagaIterator {
    yield put(updateApplicationPhoneShownByCodeSuccess({
        ...application,
        phoneShown: true
    }));
    yield call(Api.updateApplicationPhoneShown, application.apartmentId, application.applicationId);
    // yield put(updateApplicationPhoneShownByCodeSuccess(updatedApplication))
}
export function* handleEditApplication({ payload: { formData, resolve, reject } }: any): SagaIterator {
    try {
        yield call(validateIsAdmin);
        const apartmentId: string = yield select(selectContentIdFromPathname);
        const applicationId: string = yield select(selectUserIdFromPathname);
        const editedApplication: CreateApplicationData = formData;
        if (editedApplication.adminNotes) {
            delete editedApplication.adminNotes;
        }
        /*if (editedApplication.apartmentCode) {
          delete editedApplication.apartmentCode
        }*/
        const updatedApplication = yield call(Api.editApplication, apartmentId, applicationId, editedApplication);
        yield put(updateApplicationSuccess(updatedApplication));
        yield put(editApplicationSuccess());
        yield call(getHistory().push, `/apartments` as any);
        resolve();
    }
    catch (error: any) {
        yield put(editApplicationFail((error as any).message));
        yield call(reject, new SubmissionError({ _error: i18n.t('apartment.actions.formFail') }));
    }
}
export function* handleUpdateApplicationAdminNotes({ payload: { formData, resolve, reject, args } }: any): SagaIterator {
    try {
        yield call(validateIsAdmin);
        // admin view uses selectedApartmentId, reservationsList from path
        const apartmentId: string = yield select(selectSelectedApartmentId);
        const { applicationId } = args;
        const application = yield call(Api.updateApplicationAdminNotes, apartmentId, applicationId, formData);
        toast.info(i18n.t('apartment.adminInfo.success'), { autoClose: 15000 });
        yield put(updateApplicationSuccess(application));
        yield put(updateApplicationAdminNotesSuccess());
        resolve();
    }
    catch (error: any) {
        yield put(updateApplicationAdminNotesFail((error as any).message));
        yield call(reject, new SubmissionError({ _error: i18n.t('apartment.actions.formFail') }));
    }
}
export function* handleUpdateApplicationStatus({ payload: { formData, resolve, reject } }: any): SagaIterator {
    try {
        const { message, applicationId } = formData;
        const apartmentId: string = yield select(selectSelectedApartmentId);
        const application = yield call(Api.updateApplicationStatus, apartmentId, applicationId, {
            message,
            status: 'manual'
        });
        toast.info(i18n.t('adminApartments.applications.updateStatusSuccess'), {
            autoClose: 5000
        });
        yield put(updateApplicationStatusSuccess(application));
        resolve();
    }
    catch (error: any) {
        yield put(updateApplicationStatusFail((error as any).message));
        yield call(reject, new SubmissionError({ _error: i18n.t('apartment.actions.formFail') }));
    }
}
export function* handleSelectApplications(): SagaIterator {
    try {
        // const userId: string = yield select(selectUserIdFromPathname);
        // const apartmentId: string = yield select(selectContentIdFromPathname);
        // yield call(getHistory().push, {
        //   pathname: `/users/${userId}/apartments/${apartmentId}/showings`,
        // });
        yield put(selectApplicationsSuccess());
    }
    catch (error: any) {
        yield put(selectApplicationsFail((error as any).message));
    }
}
export function* handleUpdateApplicationStatusByCode({ payload: { formData, resolve, reject } }: FormPayloadParameters<UpdateStatusByCodeFormData>): SagaIterator {
    try {
        const code: string = yield select(selectApplicationCodeFromPathname);
        const stateReserveData: ReserveData | Record<string, never> = yield select(selectReserveData);
        const censoredApplication: CensoredApplication = yield call(Api.updateApplicationStatusByCode, code, {
            ...formData,
            showing: stateReserveData.reserved ||
                (Array.isArray(stateReserveData.invited) &&
                    stateReserveData.invited.length > 0 &&
                    stateReserveData.invited[0]) ||
                undefined
        });
        const reserveData = {
            ...stateReserveData,
            application: censoredApplication
        };
        const dialogId = censoredApplication.status === 'noAvailableTimes'
            ? 'ConfirmNoAvailableTimes'
            : 'ConfirmNotInterested';
        // commented out cause showings get deleted now instead in the apis
        // if (dialogId === 'ConfirmNotInterested') {
        //   const showingIds = Array.isArray(stateReserveData.invited)
        //     ? stateReserveData.invited.map((showing: Showing) => showing.showingId)
        //     : []
        //   if (showingIds.length) {
        //     yield call(ShowingsApi.cancelShowing, {
        //       applicationCode: code,
        //       showingIds
        //     })
        //   }
        // }
        yield put(closeDialog(dialogId));
        yield call(getHistory().push, `/reserve/${code}/suggest` as any);
        yield put(updateApplicationStatusByCodeSuccess(reserveData));
        resolve();
    }
    catch (error: any) {
        yield call(reject, new SubmissionError({
            _error: i18n.t('error.application.updateStatusFail')
        }));
        yield put(updateApplicationStatusByCodeFail((error as any).message));
    }
}
export function* handleGetExportedLeadDetail({ payload: leadId }: {
    payload: string;
}): SagaIterator {
    try {
        const leadData: PartnerLead = yield call(Api.getLeadData, leadId);
        yield put(getApartmentLeadSuccess({ leadId, leadData }));
    }
    catch (error: any) {
        yield put(getApartmentLeadFail((error as any).message));
    }
}
export function* handleGetConversationDetail({ payload: applicationId }: {
    payload: string;
}): SagaIterator {
    try {
        const conversationData: any = yield call(Api.getConversationData, applicationId);
        yield put(getConversationSuccess({ applicationId, conversationData }));
    }
    catch (error: any) {
        yield put(getConversationFail((error as any).message));
    }
}
function* watchApplicationActions(): SagaIterator {
    yield all([
        takeLatest(getApplication, handleGetApplication),
        takeLatest(listApplications, handleListApplications),
        takeLatest(getApartmentApplications, handleGetApartmentApplications),
        takeLatest(getApartmentLead, handleGetExportedLeadDetail),
        takeLatest(getConversation, handleGetConversationDetail),
        takeLatest(acceptApplication, handleAcceptApplication),
        takeLatest(rejectApplication, handleRejectApplication),
        takeLatest(createApplication, handleCreateApplication),
        takeLatest(editApplication, handleEditApplication),
        takeLatest(updateApplicationAdminNotes, handleUpdateApplicationAdminNotes),
        takeLatest(updateApplicationPhoneShownByCode, handleEditApplicationPhoneShown),
        takeLatest(updateApplicationStatusByCode, handleUpdateApplicationStatusByCode),
        takeLatest(updateApplicationStatus, handleUpdateApplicationStatus),
        takeLatest(selectApplications, handleSelectApplications)
    ]);
}
export default [watchApplicationActions];
